class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    // TXC: captured once from the server-rendered (translated) default so a
    // batch add's "N items added" wording can be shown, then reliably
    // reverted for the next single-item add — the heading element itself
    // isn't part of getSectionsToRender()/re-rendered from sections, so
    // without this a batch-add label would otherwise persist indefinitely.
    this.headingTextEl = this.notification.querySelector('[data-cart-notification-heading-text]');
    this.defaultHeadingText = this.headingTextEl ? this.headingTextEl.textContent.trim() : '';

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // TXC: parsedState.txcAddedCount is our own optional field (batched
    // upsell adds only — see assets/add-to-your-build.js); a normal
    // /cart/add.js response never has it, so a regular single-item add
    // always falls through to the real default text, correctly reverting
    // any earlier batch-add wording.
    if (this.headingTextEl) {
      this.headingTextEl.textContent =
        parsedState.txcAddedCount > 1 ? parsedState.txcAddedCount + ' items added' : this.defaultHeadingText;
    }

    if (this.header) this.header.reveal();
    this.renderUpsell(parsedState.product_id || parsedState.items?.[0]?.product_id);
    this.open();
  }

  async renderUpsell(productId) {
    const container = this.notification.querySelector('[data-cart-upsell]');
    const productsContainer = this.notification.querySelector('[data-cart-upsell-products]');
    if (!container || !productsContainer) return;

    container.hidden = true;
    productsContainer.replaceChildren();

    try {
      const cartResponse = await fetch(`${routes.cart_url}.json`);
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      productId ||= cart.items?.[0]?.product_id;
      if (!productId) return;

      const recommendationsResponse = await fetch(
        `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=4&intent=related`,
      );
      if (!recommendationsResponse.ok) return;
      const recommendations = await recommendationsResponse.json();
      const cartProductIds = new Set((cart.items || []).map((item) => Number(item.product_id)));
      const products = (recommendations.products || [])
        .filter((product) => product.available && product.price > 0 && !cartProductIds.has(Number(product.id)))
        .slice(0, 2);

      if (!products.length) return;

      const currency = window.Shopify?.currency?.active || cart.currency || 'USD';
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      });

      products.forEach((product) => {
        const row = document.createElement('div');
        row.className = 'cart-notification__upsell-product';

        if (product.featured_image) {
          const image = document.createElement('img');
          image.className = 'cart-notification__upsell-image';
          image.src = product.featured_image;
          image.alt = product.title;
          image.loading = 'lazy';
          image.width = 56;
          image.height = 56;
          row.appendChild(image);
        } else {
          row.appendChild(document.createElement('span'));
        }

        const info = document.createElement('div');
        info.className = 'cart-notification__upsell-info';
        const title = document.createElement('a');
        title.className = 'cart-notification__upsell-title';
        title.href = product.url;
        title.textContent = product.title;
        const price = document.createElement('div');
        price.className = 'cart-notification__upsell-price';
        price.textContent = formatter.format(product.price / 100);
        info.append(title, price);

        const view = document.createElement('a');
        view.className = 'cart-notification__upsell-view button button--secondary';
        view.href = product.url;
        view.textContent = 'View';
        view.setAttribute('aria-label', `View ${product.title}`);

        row.append(info, view);
        productsContainer.appendChild(row);
      });

      container.hidden = false;
    } catch (error) {
      // Recommendations are optional; the cart confirmation must always work.
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
